import {
  Coach,
  LimitedAccessTime,
} from "@/types";
import { Staff } from "@/types/staff";

import { RegisterSession } from "@/types/pos/register";

import { SalesReport } from "@/types/pos/sales-history";
import { MemberTableDatatypes } from "@/types/member";
import Papa from "papaparse";
import { formatInTimeZone } from "date-fns-tz";
import { JwtPayload } from "@/types";
import { SignJWT } from "jose";
import { exportPDF } from "./utils/pdf";
const { VITE_JWT_EXPIRATION, VITE_JWT_SECRET_KEY } = import.meta.env;
import { parsePhoneNumberFromString } from "libphonenumber-js";
import { Transaction, TransactionTable } from "@/types/pos/transaction";
// Your secret key as a TextEncoder-encoded Uint8Array for the browser
const secret = new TextEncoder().encode(VITE_JWT_SECRET_KEY);

// Function to create a JWT with strict typing
export async function createJWT(
  payload: JwtPayload,
  expiration = VITE_JWT_EXPIRATION
): Promise<string> {
  return await new SignJWT(payload)
    .setProtectedHeader({ alg: "HS256" })
    .setExpirationTime(expiration)
    .sign(secret);
}

export const daysOrder = [
  "monday",
  "tuesday",
  "wednesday",
  "thursday",
  "friday",
  "saturday",
  "sunday",
] as const;

// Helper function to ensure UTC parsing
export const parseAsUTC = (value: any): Date => {
  if (!value) return new Date();

  if (typeof value === "string") {
    // If it's a string without timezone info, treat it as UTC
    if (
      !value.includes("Z") &&
      !value.includes("+") &&
      !value.includes("-", value.indexOf("T"))
    ) {
      // Add 'Z' to indicate UTC if not already present
      const utcString =
        value.replace(" ", "T") + (value.includes("T") ? "Z" : "T00:00:00Z");
      return new Date(utcString);
    }
  }

  return new Date(value);
};

export const displayDate = (value: any) => {
  if (!value) return "";
  const systemTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const date = parseAsUTC(value);
  return formatInTimeZone(date, systemTimezone, "dd-MMM-yyyy");
};

export const displayDateTime = (value: any) => {
  if (!value) return "";
  const systemTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const date = parseAsUTC(value);
  return formatInTimeZone(date, systemTimezone, "dd-MMM-yyyy hh:mma");
};

export const displayTime = (value: any) => {
  if (!value) return "";
  const systemTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

  // If value is just a time string (HH:mm:ss), append it to a dummy date
  if (typeof value === "string" && value.match(/^\d{2}:\d{2}(:\d{2})?$/)) {
    value = `1970-01-01T${value}Z`;
  }

  const date = parseAsUTC(value);
  return formatInTimeZone(date, systemTimezone, "hh:mm a");
};

export const convert24HourTo12Hour = (time?: string): string => {
  if (!time || !time.match(/^([01]?[0-9]|2[0-3]):[0-5][0-9]$/)) {
    return "";
  }

  const [hours, minutes] = time.split(":").map(Number);
  const period = hours >= 12 ? "PM" : "AM";
  const displayHours = hours % 12 || 12; // Convert 0 to 12 for 12 AM
  const displayMinutes = minutes.toString().padStart(2, "0");

  return `${displayHours}:${displayMinutes} ${period}`;
};

export const displayAddress = (
  address_1?: string | null,
  address_2?: string | null,
  zipCode?: string | null,
  country?: string | null
): string => {
  const parts = [address_1, address_2, zipCode, country];

  // Filter out null, undefined, or empty strings
  const filteredParts = parts.filter((part) => part && part.trim());

  // If no valid parts, return ""
  return filteredParts.length > 0 ? filteredParts.join(", ") : "";
};

export const downloadCSV = <T>(
  data: T[],
  fileName: string,
  mapper: (item: T) => object
) => {
  const filteredData = data.map(mapper);

  const csv = Papa.unparse(filteredData);
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const link = document.createElement("a");
  link.href = URL.createObjectURL(blob);
  link.setAttribute("download", fileName);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

export function replaceUnderscoreWithSpace(inputString: string) {
  return inputString.replace(/_/g, " "); // Replace all underscores with spaces
}
export const capitalizeFirstLetter = (str: string) =>
  str ? str.charAt(0).toUpperCase() + str.slice(1) : str;

export const membersMapper = (member: MemberTableDatatypes) => ({
  "Member Id": member.own_member_id,
  "Member Name": `${capitalizeFirstLetter(
    member.first_name ?? ""
  )} ${capitalizeFirstLetter(member.last_name ?? "")}`,
  "Email": `${member.email}`,
  "Phone Number": `${
    member.mobile_number && member.mobile_number.toString().length > 3
      ? `${member.mobile_number}`
      : ""
  }`,
  "Business Name": !member.is_business &&
  member.business_id == undefined ? "" :
   capitalizeFirstLetter(member.business_name ?? ""),
  Status: capitalizeFirstLetter(member.client_status ?? ""),
  "Activation Date": displayDate(member.activated_on) ?? "",
  "Last Check In": displayDateTime(member.check_in) ?? "",
  "Last Login": displayDateTime(member.last_online) ?? "",
});

export const staffMapper = ({
  own_staff_id,
  first_name,
  last_name,
  email,
  mobile_number,
  activated_on,
  status,
  role_name,
  last_checkin,
  last_online,
}: Staff) => ({
  "Staff Id": own_staff_id,
  "Staff Name": `${capitalizeFirstLetter(
    first_name ?? ""
  )} ${capitalizeFirstLetter(last_name ?? "")}`,
  Email: `${email}`,
  "Phone Number": `${
    mobile_number && mobile_number.toString().length > 3
      ? `${mobile_number}`
      : ""
  }`,
  "Activation Date": displayDate(activated_on) ?? "",
  Role: capitalizeFirstLetter(role_name ?? ""),
  Status: capitalizeFirstLetter(status ?? "") ?? "",
  "Last Check In": displayDateTime(last_checkin) ?? "",
  "Last Login": displayDateTime(last_online) ?? "",
});
export const coachMapper = ({
  own_coach_id,
  first_name,
  last_name,
  activated_on,
  coach_status,
  check_in,
  last_online,
}: Coach) => ({
  "Coach Id": own_coach_id,
  "Coach Name": `${capitalizeFirstLetter(
    first_name ?? ""
  )} ${capitalizeFirstLetter(last_name ?? "")}`,
  "Activation Date": displayDate(activated_on) ?? "",
  Status: capitalizeFirstLetter(coach_status ?? ""),
  "Last Check In": displayDateTime(check_in) ?? "",
  "Last Login": displayDateTime(last_online) ?? "",
});

export const sessionMapper = ({
  id,
  opening_time,
  opening_balance,
  closing_time,
  closing_balance,
  discrepancy,
  notes,
  created_by,
  created_date,
}: RegisterSession) => ({
  "Session ID": id,
  "Opening Time": displayDateTime(opening_time) ?? "",
  "Opening Balance": displayValue(opening_balance?.toString()) ?? "",
  "Closing Time": displayDateTime(closing_time) ?? "",
  "Closing Balance": displayValue(closing_balance?.toString()) ?? "",
  Discrepancy: displayValue(discrepancy?.toString()) ?? "",
  Notes: capitalizeFirstLetter(notes?.toString() ?? ""),
  "Created By": capitalizeFirstLetter(created_by),
  "Created Date": displayDate(created_date) ?? "",
});

// Helper function to check if current user has owner role
export const hasFullAccessToMemberNIC = () => {
  try {
    const hideMemberNIC = (() => {
      try {
        return (
          JSON.parse(localStorage.getItem("accessLevels") as string)
            .hide_member_nic ?? "no_access"
        );
      } catch {
        return "no_access";
      }
    })();
    
    if (hideMemberNIC =="full_access") {
      return true;
    }
    return false;
  } catch (error) {
    console.error("Error checking user role:", error);
    return false;
  }
};

export const saleHistoryMapper = ({
  receipt_number,
  tax_number,
  member_name,
  member_nic,
  sub_total,
  tax_amount,
  items,
  discount,
  total,
  status,
  staff_name,
  created_at,
  srb_invoice_id,
  payments,
  counter_name,
  is_business,
}: SalesReport) => {
  const isOwner = hasFullAccessToMemberNIC();

  // Build object field-by-field in desired column order
  const mappedData: Record<string, any> = {
    "TXN Number": receipt_number,
    "SRB Number": displayValue(srb_invoice_id) ?? "",
    "Member Name": capitalizeFirstLetter(member_name) ?? ""
  };

  // Insert CNIC/NTN column immediately after member name if user is owner
  if (isOwner) {
    mappedData["CNIC/NTN"] = displayValue(is_business ? member_nic : formatNIC(member_nic)) ?? "";
  }

  Object.assign(mappedData, {
    // "Income Category": Array.from(new Set(items.map((item) => item.income_category_name).filter(Boolean))).join(", "),
    "Payment Method": Array.from(new Set(payments?.map((item) => item.payment_method).filter(Boolean))).join(", "),
    "Counter Name": counter_name,
    "Net Price": displayValue(sub_total) ?? "",
    "Discount Amount": displayValue(discount) ?? "",
    "Subtotal": displayValue(Number(sub_total) - Number(discount)) ?? "",
    "Tax Amount": displayValue(tax_amount) ?? "",
    "Total Amount": displayValue(total) ?? "",
    "Status": capitalizeFirstLetter(status),
    "Created By": capitalizeFirstLetter(staff_name),
    "Created Date": displayDate(created_at) ?? "",
  });

  return mappedData;
};

// PDF Export function for sales report
export const downloadPDF = async (
  data: TransactionTable,
  filename: string = "sales_report.pdf",
  title: string = "Sales Report",
  dateRange?: {
    start_date?: string;
    end_date?: string;
  },
  orientation: 'portrait' | 'landscape' = 'portrait',
  template: 'sales-report' | 'daily-cash-report' = 'sales-report'
) => {
  try {
    // Calculate summary
    const revenueSummary = (data as any).revenue_summary;
    const summary = {
      // For daily-cash-report, use revenue_summary.total_sales if available, otherwise use summary.total
      totalSales: template === 'daily-cash-report' && revenueSummary?.total_sales 
        ? revenueSummary.total_sales 
        : (data.summary?.total as number),
      totalRefunds: data.summary?.refund_total as number,
      totalTax: data.summary?.tax as number,
      netAmount: data.summary?.sub_total as number,
      filteredCount: data.filtered_counts as number,
      totalCount: data.total_counts as number,
      payment_methods: data.summary?.payment_methods,
      // Include revenue_summary for daily-cash-report template
      revenue_summary: revenueSummary
    };

  
    await exportPDF(template, {
      data: data.data as Transaction[],
      filename,
      title,
      orientation,
      dateRange,
      summary
    });
  } catch (error) {
    console.error("PDF export failed:", error);
    throw error;
  }
};

export const initialValue = {
  limit: 10,
  offset: 0,
  sort_order: "desc",
  sort_key: "id",
  type: "generic",
};

export const displayValue = (
  value: string | number | undefined | null,
  isPhoneNumber?: boolean
) => {
  if (isPhoneNumber) {
    const stringValue = value?.toString() ?? "";
    if (stringValue.length <= 4) {
      return "";
    }
  }
  return value === null ||
    value === undefined ||
    (typeof value === "string" && value.trim() === "")
    ? ""
    : value;
};

// Function to extract all links
export function extractLinks(data: any[]) {
  let links: string[] = [];

  data.forEach((item: any) => {
    if (item.link && item.link != "/") {
      links.push(item.link); // Add current link
    }

    // Recursively extract links from children if any
    if (item.children && item.children.length > 0) {
      links = links.concat(extractLinks(item.children));
    }
  });

  return links;
}

export const saveToLocalStorage = (key: string, value: any) => {
  localStorage.setItem(key, JSON.stringify(value));
};

export const formatDate = (date: Date | string | null): string | null => {
  if (!date) return null; // Handle null or undefined dates

  const d = new Date(date);

  // Check if the date is valid
  if (isNaN(d.getTime())) {
    console.error("Invalid date:", date);
    return null; // Return null for invalid dates
  }

  const year: number = d.getFullYear();
  const month: string = String(d.getMonth() + 1).padStart(2, "0"); // Months are 0-based
  const day: string = String(d.getDate()).padStart(2, "0");

  return `${year}-${month}-${day}`;
};

export const formatNIC = (value?: string) => {
  // Return null if the value is empty, null, or undefined
  if (!value) {
    return "";
  }

  const numericValue = value.replace(/\D/g, "");

  // Add dashes at positions 5 and 12 if length allows
  if (numericValue.length <= 5) {
    return numericValue;
  } else if (numericValue.length <= 12) {
    return `${numericValue.slice(0, 5)}-${numericValue.slice(5)}`;
  } else {
    return `${numericValue.slice(0, 5)}-${numericValue.slice(
      5,
      12
    )}-${numericValue.slice(12, 13)}`;
  }
};
export const roundToTwoDecimals = (value: number) =>
  Math.floor(value * 100) / 100;

export const inValidToken = (token: string | undefined) => {
  return token !== undefined && token.trim().length > 0;
};

export const formatToPKR = (amount: number | null | undefined) => {
  if (amount === null || amount === undefined) {
    return "";
  }

  return new Intl.NumberFormat("en-PK", {
    style: "currency",
    currency: "PKR",
    minimumFractionDigits: 2,
  })
    .format(amount)
    .replace("PKR", "Rs. ");
};

// Format large numbers for chart Y-axis (compact notation)
export const formatChartValue = (value: number | null | undefined) => {
  if (value === null || value === undefined || value === 0) {
    return "Rs. 0";
  }

  const absValue = Math.abs(value);
  
  if (absValue >= 1000000000) {
    // Billions
    return `Rs. ${(value / 1000000000).toFixed(1)}B`;
  } else if (absValue >= 1000000) {
    // Millions
    return `Rs. ${(value / 1000000).toFixed(1)}M`;
  } else if (absValue >= 1000) {
    // Thousands
    return `Rs. ${(value / 1000).toFixed(1)}K`;
  } else {
    // Less than 1000
    return `Rs. ${value.toFixed(0)}`;
  }
};

// Format numbers for chart tooltips (full format)
export const formatChartTooltip = (value: number | null | undefined) => {
  if (value === null || value === undefined) {
    return "Rs. 0";
  }
  
  return new Intl.NumberFormat("en-PK", {
    style: "currency",
    currency: "PKR",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  })
    .format(value)
    .replace("PKR", "Rs. ");
};

export function cleanLimitedAccessTime(
  limitedAccessTime: LimitedAccessTime
): LimitedAccessTime {
  const cleanedAccessTime: LimitedAccessTime = {};

  for (const [day, timeSlots] of Object.entries(limitedAccessTime)) {
    const filteredSlots = timeSlots.filter(
      (slot) => slot.from_time !== "" || slot.to_time !== ""
    );

    if (filteredSlots.length > 0) {
      cleanedAccessTime[day] = filteredSlots;
    }
  }

  return cleanedAccessTime;
}

export const replaceNullWithDefaults = (
  data: LimitedAccessTime
): LimitedAccessTime =>
  daysOrder.reduce(
    (acc, day) => ({
      ...acc,
      [day]: data[day] ?? [{ from_time: "", to_time: "" }],
    }),
    {} as LimitedAccessTime
  );

export function validateSchedule(schedule: LimitedAccessTime): {
  isValid: boolean;
  message: string;
} {
  const invalidDays: string[] = [];

  Object.entries(schedule).forEach(([day, slots]) => {
    let totalMinutes = 0;

    for (const slot of slots) {
      const { from_time, to_time } = slot;

      if (!from_time || !to_time) continue;

      const [fromHours, fromMinutes] = from_time.split(":").map(Number);
      let [toHours, toMinutes] = to_time.split(":").map(Number);

      // Handle the case where to_time is midnight (00:00)
      if (to_time === "00:00") {
        toHours = 24; // Treat as 24:00 for calculation purposes
      }

      const fromTotalMinutes = fromHours * 60 + fromMinutes;
      const toTotalMinutes = toHours * 60 + toMinutes;

      let duration = toTotalMinutes - fromTotalMinutes;
      if (duration < 0) {
        // If duration is negative, it means the slot spans over midnight
        duration += 24 * 60;
      }

      totalMinutes += duration;
    }

    // If total minutes exceed 24 hours, add the day to invalidDays
    if (totalMinutes > 24 * 60) {
      invalidDays.push(day);
    }
  });

  const isValid = invalidDays.length === 0;

  const message = isValid
    ? "All days are valid."
    : `${invalidDays
        .map((day) => capitalize(day))
        .join(", ")} cannot have more than 24 hours.`;

  return { isValid, message };
}

export function capitalize(word: string): string {
  return word.charAt(0).toUpperCase() + word.slice(1);
}

export function getDomainPart(): string | null {
  try {
    const domain = window.location.host; // e.g., "atmosphere.fitnfi.com" or "localhost:3000"

    // Check if on localhost
    if (domain.startsWith("localhost")) {
      return import.meta.env.VITE_DEFAULT_DOMAIN; // Use a predefined environment variable
    }

    // Regular expression to extract domain part
    const regex = /(?:https?:\/\/)?(?:www\.)?([^/]+)/;
    const match = regex.exec(domain);

    return match ? match[1] : null;
  } catch (error) {
    console.error("Error extracting domain:", error);
    return null; // Fallback value in case of unexpected issues
  }
}

// export const generateQueryString = (criteria: SearchCriteriaType): string => {
//   const params = new URLSearchParams();
//   for (const [key, value] of Object.entries(criteria)) {
//     if (value !== undefined && value !== null) {
//       params.append(key, value);
//     }
//   }
//   return params.toString();
// };

type SearchCriteria = Record<
  string,
  string | number | (string | number)[] | null | undefined
>;

export const generateQueryString = (criteria: SearchCriteria): string => {
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(criteria)) {
    if (value !== undefined && value !== null && value !== "") {
      if (Array.isArray(value)) {
        value.forEach((val) => params.append(key, val.toString()));
      } else {
        params.append(key, value.toString());
      }
    }
  }
  return params.toString();
};
export function debounce<T extends (...args: any[]) => void>(
  func: T,
  delay: number
): T {
  let timeout: ReturnType<typeof setTimeout>;
  return ((...args: Parameters<T>) => {
    clearTimeout(timeout);
    timeout = setTimeout(() => func(...args), delay);
  }) as T;
}

export function formatTo12Hour(time: string): string {
  // Parse the hours and minutes
  const [hoursStr, minutes] = time.split(":");
  const hours = Number.parseInt(hoursStr, 10);

  // Determine if it's AM or PM
  const period = hours >= 12 ? "PM" : "AM";

  // Convert hours to 12-hour format
  const hours12 = hours % 12 || 12;

  // Return the formatted time
  return `${hours12}:${minutes} ${period}`;
}

export const formatPhoneNumber = (phoneNumber: string): string => {
  if (!phoneNumber || phoneNumber.length < 5) return ""; // Handle empty and short inputs

  // If the number starts with '92' but isn't prefixed with '+', assume it's from Pakistan
  let parsedNumber = parsePhoneNumberFromString(
    phoneNumber.startsWith("92") ? `+${phoneNumber}` : phoneNumber
  );

  return parsedNumber ? parsedNumber.formatInternational() : "";
};

export const passwordPattern =
  /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?~\[\]\|?]).{8,}$/;

export const getInitials = (name: string) => {
  if (!name) return "";
  const nameParts = name.trim().split(" ");
  if (nameParts.length === 0) return ""; // Return empty if no name parts

  const firstInitial = nameParts[0].charAt(0).toUpperCase(); // First initial
  const lastInitial =
    nameParts.length > 1
      ? nameParts[nameParts.length - 1].charAt(0).toUpperCase()
      : ""; // Last initial

  return `${firstInitial}${lastInitial}`; // Combine initials
};

export const erpLogsMapper = (log: {
  transaction_number: string;
  bucket_no: string;
  amount: number;
  status: string;
  created_at: string;
  message?: string;
  transaction_id?: number;
}) => ({
  "Transaction Number": log.transaction_number,
  "Bucket No": displayValue(log.bucket_no),
  Amount: displayValue(log.amount),
  Status: capitalizeFirstLetter(log.status),
  "Created Date": displayDateTime(log.created_at),
  "Message": displayValue(log.message),
});

export const decodeJwtToken = (token: string) => {
  try {
    // Split the token into parts
    const [, payloadBase64] = token.split(".");

    // Decode the payload
    const payload = JSON.parse(atob(payloadBase64));

    return payload;
  } catch (error) {
    console.error("Error decoding JWT token:", error);
    return null;
  }
};
